import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface ArticleItem {
  id: number;
  title: string;
  category: string;
  views: number;
}

export default function ArticleRanking() {
  const [articles, setArticles] = useState<ArticleItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRanking = async () => {
      try {
        const { data } = await supabase
          .from('cms_news')
          .select('id, title, category, view_count')
          .eq('is_published', true)
          .order('view_count', { ascending: false })
          .limit(8);

        if (data && data.length > 0) {
          setArticles(data.map((d) => ({
            id: d.id,
            title: d.title,
            category: d.category,
            views: d.view_count || 0,
          })));
        }
      } catch (err) {
        console.error('Failed to fetch article ranking:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRanking();
  }, []);

  const maxViews = articles.length > 0 ? Math.max(...articles.map((a) => a.views), 1) : 1;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">文章访问排行</h3>
          <p className="text-xs text-gray-400 mt-0.5">按总访问量排序</p>
        </div>
        <span className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded-full">累计数据</span>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-start gap-3 animate-pulse">
              <div className="w-6 h-6 bg-gray-100 rounded-md flex-shrink-0"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-100 rounded mb-2 w-3/4"></div>
                <div className="h-2 bg-gray-100 rounded w-full"></div>
              </div>
            </div>
          ))}
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <i className="ri-article-line text-3xl mb-2 block"></i>
          <p className="text-xs">暂无文章数据</p>
        </div>
      ) : (
        <div className="space-y-4">
          {articles.map((article, idx) => (
            <div key={article.id} className="flex items-start gap-3">
              <div
                className={`w-6 h-6 flex items-center justify-center rounded-md text-xs font-bold flex-shrink-0 mt-0.5 ${
                  idx === 0
                    ? 'bg-amber-500 text-white'
                    : idx === 1
                    ? 'bg-gray-400 text-white'
                    : idx === 2
                    ? 'bg-amber-700 text-white'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {idx + 1}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <p className="text-sm text-gray-700 font-medium leading-snug line-clamp-2 flex-1">
                    {article.title}
                  </p>
                  <div className="flex flex-col items-end flex-shrink-0">
                    <span className="text-sm font-bold text-gray-800">
                      {article.views.toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-400">次浏览</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">
                    {article.category}
                  </span>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-teal-500 to-teal-400 rounded-full transition-all duration-500"
                      style={{ width: `${(article.views / maxViews) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
